"use client";

import { UnlockReportOptions } from "@saas/ai-reports/components/UnlockReportOptions";
import { useSession } from "@saas/auth/hooks/use-session";
import { Button } from "@ui/components/button";
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	BuildingIcon,
	CalendarIcon,
	CheckIcon,
	CreditCardIcon,
	DollarSignIcon,
	EyeOffIcon,
	FileTextIcon,
	GiftIcon,
	SparklesIcon,
	TrendingUpIcon,
	UsersIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { formatAiReportPrice } from "../lib/api";
import { usePurchaseFlow } from "../lib/api";
import { DemoReportCard } from "./DemoReportCard";
import { PurchaseButton } from "./PurchaseButton";

interface Report {
	id: string;
	publicToken: string;
	domain: string;
	status?: string;
	consistencyScore?: number | null;
	purchaseId?: string | null;
	property: {
		// PERSON subjects have no domain
		domain: string | null;
		subjectType?: string;
	};
}

interface DemoReport {
	id: string;
	publicToken: string;
	domain: string;
	snapshot?: {
		data: {
			name?: string;
			tagline?: string;
			category?: string;
		};
	};
	property: {
		domain: string | null;
		companyName?: string | null;
		subjectType?: string;
		logo?: string | null;
	};
}

interface UnlockPageViewProps {
	report: Report;
	demoReports: DemoReport[];
}

export function UnlockPageView({ report, demoReports }: UnlockPageViewProps) {
	const t = useTranslations();
	const price = formatAiReportPrice();
	const { user, loaded } = useSession();
	const { handlePurchase, isLoading: isPurchasing } = usePurchaseFlow(
		report.publicToken,
	);

	const isLoggedIn = !!user;
	const isPerson = report.property?.subjectType === "PERSON";
	const isCategory = report.property?.subjectType === "CATEGORY";

	// A handful of unlock-page strings talk about "your brand" / "your website";
	// person and category reports read those from a person.* / category.*
	// override block instead. A category has no focal brand at all, so its copy
	// talks about the brands AI names in the market.
	const subjectT = (key: string, values?: Record<string, any>) =>
		t(
			(isPerson
				? `marketing.aiCreditReport.unlockPage.person.${key}`
				: isCategory
					? `marketing.aiCreditReport.unlockPage.category.${key}`
					: `marketing.aiCreditReport.unlockPage.${key}`) as any,
			values,
		);

	// Don't pass organizationId - let backend auto-detect from property ownership
	// This avoids session errors on public pages and ensures correct organization

	return (
		<div className="min-h-screen bg-background">
			{/* Header with back button */}
			<div className="border-b bg-white">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<Link
						href={`/reports/${report.publicToken}`}
						className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
					>
						<ArrowLeftIcon className="w-4 h-4" />
						{t("marketing.aiCreditReport.unlockPage.backToReport")}
					</Link>
				</div>
			</div>

			<main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
				{/* Hero Section */}
				<section className="max-w-4xl mx-auto text-center mb-16">
					<div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium mb-6">
						<SparklesIcon className="w-4 h-4" />
						<span>Complete AI Visibility Insights</span>
					</div>

					<h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4">
						{t("marketing.aiCreditReport.unlockPage.title")}
					</h1>

					<p className="text-xl text-muted-foreground mb-8">
						{t("marketing.aiCreditReport.unlockPage.subtitle")}
					</p>

					{/* Show different unlock options based on login status */}
					<div className="max-w-4xl mx-auto mb-8">
						{isLoggedIn ? (
							<UnlockReportOptions
								reportPublicToken={report.publicToken}
								organizationId={undefined} // Backend will auto-detect from property
								onStripeCheckout={handlePurchase}
								isCheckoutLoading={isPurchasing}
							/>
						) : (
							/* Dual CTA for non-logged-in users */
							<div className="grid md:grid-cols-2 gap-6">
								{/* Option 1: Pay Once */}
								<div className="bg-white rounded-2xl border-2 border-blue-200 p-8 text-left hover:shadow-lg transition-shadow relative">
									<div className="absolute -top-3 left-6">
										<span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
											{t(
												"marketing.aiCreditReport.unlockPage.dualCta.instant.badge",
											)}
										</span>
									</div>

									<div className="flex items-center gap-3 mb-4">
										<div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
											<CreditCardIcon className="w-6 h-6 text-blue-600" />
										</div>
										<div>
											<h3 className="text-xl font-bold text-gray-900">
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.instant.title",
												)}
											</h3>
											<p className="text-3xl font-bold text-blue-600">
												{price}
											</p>
										</div>
									</div>

									<ul className="space-y-2 mb-6">
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.instant.benefit1",
												)}
											</span>
										</li>
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.instant.benefit2",
												)}
											</span>
										</li>
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.instant.benefit3",
												)}
											</span>
										</li>
									</ul>

									<PurchaseButton
										reportPublicToken={report.publicToken}
										size="lg"
										variant="primary"
										className="w-full"
										directCheckout={true}
										reportId={report.id}
										domain={report.domain}
										consistencyScore={
											report.consistencyScore || undefined
										}
									/>
								</div>

								{/* Option 2: Free Consultation */}
								<div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border-2 border-purple-200 p-8 text-left hover:shadow-lg transition-shadow relative">
									<div className="absolute -top-3 left-6">
										<span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
											<GiftIcon className="w-3 h-3" />
											{t(
												"marketing.aiCreditReport.unlockPage.dualCta.consultation.badge",
											)}
										</span>
									</div>

									<div className="flex items-center gap-3 mb-4">
										<div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
											<UsersIcon className="w-6 h-6 text-purple-600" />
										</div>
										<div>
											<h3 className="text-xl font-bold text-gray-900">
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.consultation.title",
												)}
											</h3>
											<p className="text-3xl font-bold text-purple-600">
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.consultation.price",
												)}
											</p>
										</div>
									</div>

									<ul className="space-y-2 mb-6">
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.consultation.benefit1",
												)}
											</span>
										</li>
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.consultation.benefit2",
												)}
											</span>
										</li>
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.consultation.benefit3",
												)}
											</span>
										</li>
										<li className="flex items-center gap-2 text-sm text-gray-600">
											<CheckIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />
											<span>
												{t(
													"marketing.aiCreditReport.unlockPage.dualCta.consultation.benefit4",
												)}
											</span>
										</li>
									</ul>

									<Button
										variant="primary"
										size="lg"
										className="w-full bg-purple-600 hover:bg-purple-700"
										asChild
									>
										<Link href="/demo">
											<CalendarIcon className="w-4 h-4 mr-2" />
											{t(
												"marketing.aiCreditReport.unlockPage.dualCta.consultation.button",
											)}
										</Link>
									</Button>
								</div>
							</div>
						)}
					</div>
				</section>

				{/* Benefits Grid */}
				<section className="max-w-6xl mx-auto mb-20">
					<div className="grid md:grid-cols-3 gap-8">
						{/* Benefit 1 */}
						<div className="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
							<div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
								<SparklesIcon className="w-6 h-6 text-blue-600" />
							</div>
							<h3 className="text-xl font-semibold mb-3">
								{t(
									"marketing.aiCreditReport.unlockPage.hero.benefit1.title",
								)}
							</h3>
							<p className="text-muted-foreground">
								{subjectT("hero.benefit1.description")}
							</p>
						</div>

						{/* Benefit 2 */}
						<div className="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
							<div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
								<TrendingUpIcon className="w-6 h-6 text-green-600" />
							</div>
							<h3 className="text-xl font-semibold mb-3">
								{t(
									"marketing.aiCreditReport.unlockPage.hero.benefit2.title",
								)}
							</h3>
							<p className="text-muted-foreground">
								{t(
									"marketing.aiCreditReport.unlockPage.hero.benefit2.description",
									{ price: price },
								)}
							</p>
						</div>

						{/* Benefit 3 */}
						<div className="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
							<div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
								<EyeOffIcon className="w-6 h-6 text-purple-600" />
							</div>
							<h3 className="text-xl font-semibold mb-3">
								{t(
									"marketing.aiCreditReport.unlockPage.hero.benefit3.title",
								)}
							</h3>
							<p className="text-muted-foreground">
								{t(
									"marketing.aiCreditReport.unlockPage.hero.benefit3.description",
								)}
							</p>
						</div>
					</div>
				</section>

				{/* Demo Reports Section */}
				{demoReports.length > 0 && (
					<section className="max-w-6xl mx-auto mb-20">
						<div className="text-center mb-12">
							<h2 className="text-3xl font-bold mb-4">
								{t(
									"marketing.aiCreditReport.unlockPage.demoReports.title",
								)}
							</h2>
							<p className="text-lg text-muted-foreground">
								{subjectT("demoReports.description")}
							</p>
						</div>

						<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
							{demoReports.slice(0, 6).map((demoReport) => (
								<DemoReportCard
									key={demoReport.id}
									report={demoReport}
								/>
							))}
						</div>
					</section>
				)}

				{/* Agency Section */}
				<section className="max-w-6xl mx-auto mb-20">
					<div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-2xl border border-gray-200 p-12">
						<div className="text-center mb-12">
							<div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
								<BuildingIcon className="w-4 h-4" />
								<span>For Agencies & Consultants</span>
							</div>
							<h2 className="text-3xl font-bold mb-4">
								{t(
									"marketing.aiCreditReport.unlockPage.agencies.title",
								)}
							</h2>
							<p className="text-lg text-muted-foreground">
								{t(
									"marketing.aiCreditReport.unlockPage.agencies.subtitle",
								)}
							</p>
						</div>

						<div className="grid md:grid-cols-3 gap-8 mb-8">
							{/* Agency Benefit 1 */}
							<div className="bg-white dark:bg-gray-900 rounded-lg p-6">
								<div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
									<FileTextIcon className="w-5 h-5 text-green-600" />
								</div>
								<h3 className="font-semibold mb-2">
									{t(
										"marketing.aiCreditReport.unlockPage.agencies.benefit1.title",
									)}
								</h3>
								<p className="text-sm text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.agencies.benefit1.description",
									)}
								</p>
							</div>

							{/* Agency Benefit 2 */}
							<div className="bg-white dark:bg-gray-900 rounded-lg p-6">
								<div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
									<DollarSignIcon className="w-5 h-5 text-blue-600" />
								</div>
								<h3 className="font-semibold mb-2">
									{t(
										"marketing.aiCreditReport.unlockPage.agencies.benefit2.title",
									)}
								</h3>
								<p className="text-sm text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.agencies.benefit2.description",
										{ price: price },
									)}
								</p>
							</div>

							{/* Agency Benefit 3 */}
							<div className="bg-white dark:bg-gray-900 rounded-lg p-6">
								<div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4">
									<EyeOffIcon className="w-5 h-5 text-purple-600" />
								</div>
								<h3 className="font-semibold mb-2">
									{t(
										"marketing.aiCreditReport.unlockPage.agencies.benefit3.title",
									)}
								</h3>
								<p className="text-sm text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.agencies.benefit3.description",
									)}
								</p>
							</div>
						</div>

						{/* Agency CTA */}
						<div className="bg-white dark:bg-gray-900 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-8 text-center">
							<h3 className="text-xl font-semibold mb-2">
								{t(
									"marketing.aiCreditReport.unlockPage.agencies.cta.title",
								)}
							</h3>
							<p className="text-muted-foreground mb-6">
								{t(
									"marketing.aiCreditReport.unlockPage.agencies.cta.description",
								)}
							</p>
							<Link
								href="/pricing"
								className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors"
							>
								{t(
									"marketing.aiCreditReport.unlockPage.agencies.cta.button",
								)}
								<ArrowRightIcon className="w-4 h-4" />
							</Link>
						</div>
					</div>
				</section>

				{/* FAQ Section */}
				<section className="max-w-3xl mx-auto mb-20">
					<div className="text-center mb-12">
						<h2 className="text-3xl font-bold mb-4">
							{t("marketing.aiCreditReport.unlockPage.faq.title")}
						</h2>
					</div>

					<div className="space-y-4">
						{/* FAQ Item 1 */}
						<details className="group bg-white rounded-lg border border-gray-200">
							<summary className="flex items-center justify-between w-full p-6 text-left cursor-pointer">
								<h3 className="font-semibold">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.whatIncluded.question",
									)}
								</h3>
								<div className="ml-4 flex-shrink-0">
									<ArrowRightIcon className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
								</div>
							</summary>
							<div className="px-6 pb-6">
								<p className="text-muted-foreground">
									{subjectT("faq.items.whatIncluded.answer")}
								</p>
							</div>
						</details>

						{/* FAQ Item 2 */}
						<details className="group bg-white rounded-lg border border-gray-200">
							<summary className="flex items-center justify-between w-full p-6 text-left cursor-pointer">
								<h3 className="font-semibold">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.oneTimeFee.question",
									)}
								</h3>
								<div className="ml-4 flex-shrink-0">
									<ArrowRightIcon className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
								</div>
							</summary>
							<div className="px-6 pb-6">
								<p className="text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.oneTimeFee.answer",
										{ price: price },
									)}
								</p>
							</div>
						</details>

						{/* FAQ Item 3 */}
						<details className="group bg-white rounded-lg border border-gray-200">
							<summary className="flex items-center justify-between w-full p-6 text-left cursor-pointer">
								<h3 className="font-semibold">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.howOften.question",
									)}
								</h3>
								<div className="ml-4 flex-shrink-0">
									<ArrowRightIcon className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
								</div>
							</summary>
							<div className="px-6 pb-6">
								<p className="text-muted-foreground">
									{subjectT("faq.items.howOften.answer")}
								</p>
							</div>
						</details>

						{/* FAQ Item 4 */}
						<details className="group bg-white rounded-lg border border-gray-200">
							<summary className="flex items-center justify-between w-full p-6 text-left cursor-pointer">
								<h3 className="font-semibold">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.agencies.question",
									)}
								</h3>
								<div className="ml-4 flex-shrink-0">
									<ArrowRightIcon className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
								</div>
							</summary>
							<div className="px-6 pb-6">
								<p className="text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.agencies.answer",
									)}
								</p>
							</div>
						</details>

						{/* FAQ Item 5 */}
						<details className="group bg-white rounded-lg border border-gray-200">
							<summary className="flex items-center justify-between w-full p-6 text-left cursor-pointer">
								<h3 className="font-semibold">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.refund.question",
									)}
								</h3>
								<div className="ml-4 flex-shrink-0">
									<ArrowRightIcon className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
								</div>
							</summary>
							<div className="px-6 pb-6">
								<p className="text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.refund.answer",
									)}
								</p>
							</div>
						</details>

						{/* FAQ Item 6 */}
						<details className="group bg-white rounded-lg border border-gray-200">
							<summary className="flex items-center justify-between w-full p-6 text-left cursor-pointer">
								<h3 className="font-semibold">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.technical.question",
									)}
								</h3>
								<div className="ml-4 flex-shrink-0">
									<ArrowRightIcon className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
								</div>
							</summary>
							<div className="px-6 pb-6">
								<p className="text-muted-foreground">
									{t(
										"marketing.aiCreditReport.unlockPage.faq.items.technical.answer",
									)}
								</p>
							</div>
						</details>
					</div>
				</section>

				{/* Final CTA */}
				<section className="max-w-3xl mx-auto text-center">
					<div className="bg-white rounded-2xl border-2 border-blue-200 p-12">
						<h2 className="text-2xl font-bold mb-4">
							{t(
								"marketing.aiCreditReport.unlockPage.finalCta.title",
							)}
						</h2>
						<p className="text-lg text-muted-foreground mb-8">
							{t(
								"marketing.aiCreditReport.unlockPage.finalCta.description",
								{ domain: report.domain },
							)}
						</p>
						<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
							<PurchaseButton
								reportPublicToken={report.publicToken}
								size="lg"
								variant="primary"
								className="w-full sm:w-auto"
								directCheckout={true}
								reportId={report.id}
								domain={report.domain}
								consistencyScore={
									report.consistencyScore || undefined
								}
							/>
							<span className="text-muted-foreground">
								{t(
									"marketing.aiCreditReport.unlockPage.finalCta.or",
								)}
							</span>
							<Button
								variant="outline"
								size="lg"
								className="w-full sm:w-auto border-purple-300 text-purple-700 hover:bg-purple-50"
								asChild
							>
								<Link href="/demo">
									<CalendarIcon className="w-4 h-4 mr-2" />
									{t(
										"marketing.aiCreditReport.unlockPage.finalCta.consultationButton",
									)}
								</Link>
							</Button>
						</div>
					</div>
				</section>
			</main>
		</div>
	);
}
